import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { classNames } from '@ux/component-utilities';
import Button from '@ux/button';
import XIcon from '@ux/icon/x';
import { ICONS } from './icons.js';


const UX_ALERT_CLASSNAME = 'ux-alert';

/**
 * Alert notifications component.
 *
 * Alerts may be dissmissible, or not have a close button.
 *
 * @displayName Alert
 * @class Alert
 * @public
 */
export default class Alert extends Component {
  constructor() {
    super(...arguments);
    this.state = { open: true };
  }

  close() {
    this.setState({ open: false });
    this.props.onClose && this.props.onClose();
  }
  /**
   * Render the alert component.
   *
   * @returns {ReactElement} Alert box.
   * @public
   */
  render() {
    const {
      button,
      className,
      children,
      dismissible,
      emphasis,
      ariaLabels
    } = this.props;
    const classes = [UX_ALERT_CLASSNAME, `${UX_ALERT_CLASSNAME}-${emphasis}`, className].filter(Boolean);

    // Only allow data-* attributes to pass-through
    const passThroughProps = Object.keys(this.props).reduce((curr, propName) => {
      if (propName.startsWith('data-')) {
        curr[propName] = this.props[propName];
      }
      return curr;
    }, {});

    let closeButton;
    if (dismissible) {
      closeButton = <Button
        className={ `${UX_ALERT_CLASSNAME}-close-button` }
        design={ 'inline' }
        onClick={ this.close.bind(this) }
        icon={ <XIcon/> }
        aria-label={ ariaLabels.close }/>;
    }

    const Icon = ICONS[emphasis];
    let buttonComponent;
    if (button) {
      const { className: clsx } = button.props || {};
      const buttonClassName = classNames(`${UX_ALERT_CLASSNAME}-button`, clsx);
      buttonComponent = React.cloneElement(button, { className: buttonClassName, as: 'cta', design: 'inline' });
    }

    return (this.state.open &&
      <div className={ classes.join(' ') } role='alert' { ...passThroughProps }>
        { <Icon className={ `${UX_ALERT_CLASSNAME}-icon` }/> }
        <div className={ `${UX_ALERT_CLASSNAME}-content` }>
          <div className={ `${UX_ALERT_CLASSNAME}-message` }>{ children }</div>
          { buttonComponent }
        </div>
        { closeButton }
      </div>
    );
  }
}
/**
 * The default properties.
 *
 * @type {Object}
 * @private
 */
Alert.defaultProps = {
  dismissible: true,
  ariaLabels: {
    close: 'Close'
  },
  emphasis: 'critical'
};
/**
 * Property types.
 *
 * @type {Object}
 * @private
 */
Alert.propTypes = {
  /** Adds a cta button */
  button: PropTypes.shape({ type: Button.type, props: PropTypes.object }),
  /** Additional alert detail content */
  children: PropTypes.node,
  /** Additional class name to target for positioning */
  className: PropTypes.string,
  /** Flag for whether the alert can be dismissed */
  dismissible: PropTypes.bool,
  /** Screenreader labels for sub-elements */
  ariaLabels: PropTypes.shape({
    /** Screen reader label for close button */
    close: PropTypes.string
  }),
  /** Describes the purpose of the alert */
  emphasis: PropTypes.oneOf(Object.keys(ICONS)),
  /** Callback function when alert is closed */
  onClose: PropTypes.func
};
